'use client';

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import routesfe from '../Config/RoutesFE';
import { pricingPlans, getSavePercentage } from '../Config/PricingPlans';
import configDetails from '../Config/Config';
import PlanCard from '../components copy/PlanCard';
import ContactSalesModal from '../components copy/ContactSalesModal';
import SEO from '../components copy/SEO';

import Notification from '../components copy/Notification';
import PaymentService from '../services/PaymentService';

const LandingPage = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  // Find the index of the Professional plan (or fallback to 0)
  const professionalIndex = pricingPlans.findIndex(plan => plan.name.toLowerCase() === 'professional');
  const [hoveredPlan, setHoveredPlan] = useState(professionalIndex !== -1 ? professionalIndex : 0);
  const [notification, setNotification] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Handle conversation deleted state and show notification
  useEffect(() => {
    if (location.state?.conversationDeleted) {
      const message = location.state?.deletedMessage || 'This conversation no longer exists';
      setNotification({ message, type: 'error', duration: 5000 });
      
      // Clear the state by replacing history without the state
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state]);

  // Close notification function
  const closeNotification = () => {
    setNotification(null);
  };

  // Structured data for Google
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "NoteForge",
    "applicationCategory": "ProductivityApplication",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "lowPrice": "0",
      "highPrice": "19",
      "offerCount": pricingPlans.length
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "1250"
    },
    "description": "AI-powered note-taking and transcription app for meetings, lectures, and conversations"
  };

  const features = [
    {
      id: 1,
      title: 'AI Chat Bot',
      description: 'Intelligent assistant that helps you organize notes, answer questions, and provide insights from your sessions.',
      icon: 'ri-robot-line',
      color: 'from-blue-500 to-purple-600',
      image: 'https://readdy.ai/api/search-image?query=modern%20AI%20chatbot%20interface%20with%20holographic%20elements%20floating%20in%20space%2C%20futuristic%20blue%20and%20purple%20gradient%20background%2C%20clean%20minimalist%20design%20with%20floating%20chat%20bubbles%20and%20digital%20particles&width=600&height=400&seq=aibot001&orientation=landscape'
    },
    {
      id: 2,
      title: 'Meetings',
      description: 'Schedule, manage, and collaborate seamlessly with integrated meeting tools and real-time synchronization.',
      icon: 'ri-team-line',
      color: 'from-green-500 to-teal-600',
      image: 'https://readdy.ai/api/search-image?query=professional%20virtual%20meeting%20space%20with%20floating%20holographic%20screens%20and%20conference%20elements%2C%20modern%20green%20and%20teal%20gradient%20background%2C%20collaborative%20workspace%20with%20digital%20connectivity%20nodes&width=600&height=400&seq=meeting002&orientation=landscape'
    },
    {
      id: 3,
      title: 'Session Notes',
      description: 'Capture, organize, and retrieve your notes effortlessly with advanced categorization and search capabilities.',
      icon: 'ri-file-text-line',
      color: 'from-orange-500 to-red-600',
      image: 'https://readdy.ai/api/search-image?query=digital%20note-taking%20interface%20with%20floating%20documents%20and%20organized%20content%20blocks%2C%20warm%20orange%20and%20red%20gradient%20background%2C%20modern%20typography%20and%20clean%20layout%20elements&width=600&height=400&seq=notes003&orientation=landscape'
    },
    {
      id: 4,
      title: 'Audio to Text',
      description: 'Convert audio recordings to accurate text instantly with advanced AI-powered transcription technology.',
      icon: 'ri-mic-line',
      color: 'from-pink-500 to-rose-600',
      image: 'https://readdy.ai/api/search-image?query=audio%20waveform%20visualization%20with%20text%20conversion%20elements%20floating%20in%20space%2C%20pink%20and%20rose%20gradient%20background%2C%20modern%20sound%20wave%20graphics%20and%20transcription%20interface&width=600&height=400&seq=audio004&orientation=landscape'
    },
    {
      id: 5,
      title: 'Live Transcripts',
      description: 'Real-time transcription that captures every word as it\'s spoken, perfect for meetings and lectures.',
      icon: 'ri-live-line',
      color: 'from-indigo-500 to-blue-600',
      image: 'https://readdy.ai/api/search-image?query=live%20transcription%20interface%20with%20real-time%20text%20streaming%20and%20floating%20speech%20bubbles%2C%20indigo%20and%20blue%20gradient%20background%2C%20dynamic%20typing%20animation%20and%20live%20indicator%20elements&width=600&height=400&seq=live005&orientation=landscape'
    }
  ];

  // Helper: check if user is authenticated
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('MONTHLY');

  // Load selected billing cycle from sessionStorage on mount
  useEffect(() => {
    const savedCycle = sessionStorage.getItem('noteforge_selected_billing_cycle');
    if (savedCycle === 'YEARLY' || savedCycle === 'MONTHLY') {
      setSelectedBillingCycle(savedCycle);
    }
  }, []);
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        setIsAuthenticated(!!user);
      } catch {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  // Updated Subscribe button handler to pass complete plan data
  const handleSubscribe = async (planName) => {
    if (planName === 'Free Trial') {
      navigate(routesfe.SIGNUP, { replace: true });
      return;
    }

    // For Enterprise, don't require billingCycle match since it appears in both
    let selectedPlan;
    if (planName === 'Enterprise') {
      selectedPlan = pricingPlans.find(plan => plan.name === planName);
      if (selectedPlan) {
        setShowContactModal(true);
        return;
      }
    }
    
    selectedPlan = pricingPlans.find(plan => plan.name === planName && (plan.billingCycle === selectedBillingCycle || !plan.billingCycle));
    
    if (!selectedPlan) {
      console.error('Plan not found:', planName);
      return;
    }

    // If Enterprise is selected, open Contact Sales modal instead of buy flow
    if (selectedPlan.name === 'Enterprise') {
      setShowContactModal(true);
      return;
    }

    if (!isAuthenticated) {
      localStorage.removeItem('user');
      // Redirect to login, pass intended plan for payment
      navigate(routesfe.LOGIN, { 
        state: { 
          redirect: routesfe.PAYMENT, 
          selectedPlan: selectedPlan,
          billingCycle: selectedBillingCycle 
        },
        replace: true 
      });
    } else {
      // Go directly to payment page with complete plan data
      navigate(routesfe.PAYMENT, { 
        state: { 
          selectedPlan: selectedPlan,
          billingCycle: selectedBillingCycle 
        },
        replace: true 
      });
    }
  };

  // Calculate savings percentage dynamically for the Professional plan
  const getAnnualSavingsPercentage = () => {
    const monthlyProfessional = pricingPlans.find(p => p.name === 'Professional' && p.billingCycle === 'MONTHLY');
    const yearlyProfessional = pricingPlans.find(p => p.name === 'Professional' && p.billingCycle === 'YEARLY');
    
    if (monthlyProfessional && yearlyProfessional) {
      return getSavePercentage(yearlyProfessional.price, monthlyProfessional.price);
    }
    return '';
  };

  const [showContactModal, setShowContactModal] = useState(false);

  // Include the Enterprise card when calculating layout
  const totalPlans = pricingPlans.length; // centralized pricingPlans includes Enterprise

  // Scroll to section handler
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const scrollToFooter = () => {
    const el = document.getElementById('footer');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <SEO
        title="NoteForge - AI-Powered Note Taking & Transcription App"
        description="Transform your meetings, lectures, and conversations into organized, searchable notes with NoteForge. AI-powered transcription, smart summaries, and real-time collaboration. Auto text conversation and voice recorder for all your needs."
        keywords="noteforge, noteforge.ai, noteforge ai, note taking app, AI transcription, meeting notes, voice to text, lecture notes, AI note taker, transcription app, meeting recorder, voice notes, study notes, auto text conversation, voice recorder conversation, speech to text, voice transcription, automatic note taking, meeting transcription, lecture recording, conversation recorder, audio to text converter, real-time transcription, AI meeting assistant, smart notes, dictation app, voice memo transcription"
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
          duration={notification.duration}
        />
      )}
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/85 backdrop-blur-md border-b border-gray-200/50 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Consistent Logo */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img src="/Images/noteforge-logo.png" alt="NoteForge Logo" className="nav-logo-img" style={{ width: '48px', height: '48px', borderRadius: '16px' }} />
              <div className="hidden sm:block">
                <h1 className="text-xl sm:text-2xl font-bold" style={{ color: '#6C2BD7', letterSpacing: '-0.5px' }}>
                  NOTEFORGE
                </h1>
                <p className="text-xs sm:text-sm mt-1 flex items-center gap-1" style={{ color: '#D72660', fontWeight: 600 }}>
                  ✨ Capture. Structure. Succeed.
                </p>
              </div>
              <div className="block sm:hidden">
                <h1 className="text-lg font-bold" style={{ color: '#6C2BD7', letterSpacing: '-0.5px' }}>
                  NOTEFORGE
                </h1>
              </div>
            </div>

            {/* Section Links */}
            <nav className="hidden md:flex items-center space-x-8 mr-8">
              <button
                onClick={scrollToFooter}
                className="font-serif font-bold text-lg text-gray-800 hover:text-blue-700 transition-colors tracking-wide"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('features')}
                className="font-serif font-bold text-lg text-gray-800 hover:text-blue-700 transition-colors tracking-wide"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="font-serif font-bold text-lg text-gray-800 hover:text-blue-700 transition-colors tracking-wide"
              >
                Pricing
              </button>
            </nav>
            {/* Enhanced Login/Signup Buttons */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <button 
                onClick={() => navigate(routesfe.LOGIN)}
                className="relative px-3 py-2 sm:px-6 sm:py-2.5 text-gray-700 font-semibold rounded-lg sm:rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50/50 transition-all duration-200 cursor-pointer group text-sm sm:text-base"
              >
                <span className="relative z-10">Log In</span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg sm:rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </button>
              <button 
                onClick={() => navigate(routesfe.SIGNUP)}
                className="relative px-3 py-2 sm:px-6 sm:py-2.5 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 text-white font-semibold rounded-lg sm:rounded-xl hover:shadow-lg hover:shadow-blue-500/25 hover:scale-105 transition-all cursor-pointer group overflow-hidden text-sm sm:text-base"
              >
                <span className="relative z-10 flex items-center space-x-1 sm:space-x-2">
                  <span>Sign Up</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform hidden sm:inline"></i>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-700 via-purple-700 to-indigo-800 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Enhanced Hero Section */}
      <section className="pt-24 sm:pt-32 pb-4 sm:pb-6 px-4 sm:px-6 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=abstract%20digital%20workspace%20with%20floating%20holographic%20elements%20and%20modern%20technology%20interface%2C%20soft%20blue%20and%20purple%20gradient%20background%2C%20futuristic%20note-taking%20environment%20with%20clean%20minimalist%20design&width=1920&height=1080&seq=hero001&orientation=landscape')`
          }}
        ></div>
        <div className="container mx-auto text-center relative z-10">
          <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4 sm:mb-6 leading-tight">
              NOTEFORGE
            </h1>
            <h4 className="text-xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-2">
              Capture Every Conversation
            </h4>
            <p className="text-base sm:text-xl md:text-2xl text-gray-600 mb-6 sm:mb-8 max-w-3xl mx-auto px-4 leading-relaxed">
              Noteforge turns your audio into accurate, searchable, and editable notes in real time using AI.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="pt-4 sm:pt-6 pb-12 sm:pb-20 px-4 sm:px-6">
        <div className="container mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-3 sm:mb-4 px-2">
              Powerful Features
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Discover how NOTEFORGE revolutionizes your productivity with cutting-edge AI technology
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Feature Navigation */}
            <div className="space-y-4 sm:space-y-6 order-2 lg:order-1">
              {features.map((feature, index) => (
                <div
                  key={feature.id}
                  className={`p-4 sm:p-6 rounded-xl cursor-pointer transition-all duration-300 ${
                    activeFeature === index
                      ? 'bg-white shadow-xl border-2 border-blue-200'
                      : 'bg-white/50 hover:bg-white hover:shadow-lg'
                  }`}
                  onClick={() => setActiveFeature(index)}
                >
                  <div className="flex items-start sm:items-center space-x-3 sm:space-x-4">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-r ${feature.color} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${feature.icon} text-white text-lg sm:text-xl`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-1 sm:mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Feature Image */}
            <div className="relative order-1 lg:order-2">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img
                  src={features[activeFeature].image}
                  alt={features[activeFeature].title}
                  className="w-full h-64 sm:h-80 lg:h-96 object-cover object-top transition-all duration-500"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${features[activeFeature].color} opacity-20`}></div>
              </div>
              <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center animate-pulse">
                <i className="ri-star-line text-white text-xl sm:text-2xl"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Updated to use centralized pricing */}
      <section id="pricing" className="py-12 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 px-2">
              Choose Your Plan
            </h2>
            {/* Monthly/Yearly Toggle */}
            <div className="mt-8 flex items-center justify-center">
              <div className="inline-flex rounded-full bg-gray-100 p-1 relative">
                {/* Monthly button */}
                <button
                  onClick={() => {
                    setSelectedBillingCycle('MONTHLY');
                    sessionStorage.setItem('noteforge_selected_billing_cycle', 'MONTHLY');
                  }}
                  className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap ${
                    selectedBillingCycle === 'MONTHLY'
                      ? 'bg-white text-gray-800 shadow-md'
                      : 'bg-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Monthly
                </button>
                
                {/* Annual button with badge */}
                <button
                  onClick={() => {
                    setSelectedBillingCycle('YEARLY');
                    sessionStorage.setItem('noteforge_selected_billing_cycle', 'YEARLY');
                  }}
                  className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap flex items-center gap-2 ${
                    selectedBillingCycle === 'YEARLY'
                      ? 'bg-white text-gray-800 shadow-md'
                      : 'bg-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Annual
                  <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                    save {getAnnualSavingsPercentage()}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div className={`grid gap-6 sm:gap-8 mx-auto grid-cols-1 md:grid-cols-2 lg:grid-cols-4 max-w-7xl`}>
            {pricingPlans.filter(plan => !plan.billingCycle || plan.billingCycle === selectedBillingCycle).map((plan, index) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isHighlighted={hoveredPlan === index || (hoveredPlan === null && professionalIndex === index)}
                onMouseEnter={() => setHoveredPlan(index)}
                onMouseLeave={() => setHoveredPlan(professionalIndex !== -1 ? professionalIndex : 0)}
                onSubscribe={handleSubscribe}
              />
            ))}
            {/* Enterprise is included in pricingPlans now */}
          </div>
          <ContactSalesModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} planName="Enterprise" />
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 px-2">
            Ready to Transform Your Note-Taking?
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto px-4 leading-relaxed">
            Join thousands of professionals who have revolutionized their productivity with NOTEFORGE
          </p>
        </div>
      </section>

      {/* Footer */}
  <footer id="footer" className="bg-gray-900 text-white py-8 sm:py-12 px-4 sm:px-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 sm:gap-8 items-start">
            {/* Left Section - About */}
            <div className="md:col-span-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start space-x-2 mb-3 sm:mb-4">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <i className="ri-quill-pen-line text-white text-sm sm:text-lg"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold">ABOUT NOTEFORGE</h3>
              </div>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                Revolutionizing note-taking with AI-powered technology for modern professionals.
              </p>
            </div>

            {/* Center Section - Main Text and Contact */}
            <div className="md:col-span-3 flex flex-col justify-center items-center text-center">
              <p className="text-sm sm:text-base md:text-lg text-white max-w-3xl mb-6 sm:mb-8 leading-relaxed px-2">
                Noteforge is your AI-powered note companion. Whether you’re in meetings, lectures, or interviews, capture every word and convert it into structured, shareable insights. Power up your productivity with effortless note-taking.
              </p>              
              {/* Legal Links */}
              <div className="mt-4">
                <div className="flex flex-wrap justify-center items-center gap-6 text-sm">
                  <button
                    onClick={() => navigate(routesfe.PRIVACY_POLICY)}
                    className="text-gray-300 hover:text-blue-400 transition-all duration-300 font-medium border-b border-transparent hover:border-blue-400 pb-1 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 rounded"
                  >
                    Privacy Policy
                  </button>
                  <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                  <button
                    onClick={() => navigate(routesfe.TERMS_OF_SERVICE)}
                    className="text-gray-300 hover:text-blue-400 transition-all duration-300 font-medium border-b border-transparent hover:border-blue-400 pb-1 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 rounded"
                  >
                    Terms of Service
                  </button>
                </div>
                <div className="mt-3 text-xs text-gray-500 text-center">
                  © 2026 Noteforge LLC. All rights reserved.
                </div>
                </div>
              </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default LandingPage;
